import React, {useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import AttendanceChannelService from "../../../../services/dispatch/attendance-channel-service.js";
import {useRequiredFields} from "../../../../hooks/use-required-fields.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const initialChannel = {name: "", channelDefault: false};

const AttendanceChannelForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const [state, setState] = useState(initialChannel);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);
    const nameReference = useRef(null);
    const {clearRequiredFieldError, getRequiredFieldProps, validateRequiredFields} = useRequiredFields();

    const {isLoading: isQueryLoading} = useQuery({
        queryFn: async () => {
            const data = await AttendanceChannelService.get(id).catch(() => null);
            if (!data) {
                navigate("/404");
                return;
            }

            const {channel} = data;
            setState({id: channel.id, name: channel.name ?? "", channelDefault: !!channel.channelDefault});
            return channel;
        },
        queryKey: ["dispatch-attendance-channel", id],
        enabled: !!id,
    });

    const persist = (addAfter = false) => {
        if (!validateRequiredFields([{id: "name", value: state.name, reference: nameReference}])) return;

        const payload = {name: state.name, channelDefault: state.channelDefault};

        const setSaving = addAfter ? setIsSavingAndAdding : setIsSaving;
        setSaving(true);
        const response = id ? AttendanceChannelService.update(id, payload) : AttendanceChannelService.save(payload);
        response
            .then((data) => {
                if (addAfter) {
                    if (id) {
                        projectUtils.success(true);
                        navigate(API_DESPACHO_WEB + "/attendance-channel/form", false);
                    } else {
                        projectUtils.success(false);
                        setState(initialChannel);
                    }
                    return;
                }

                projectUtils.success(true);

                if (id) {
                    navigate(API_DESPACHO_WEB + "/attendance-channel", false);
                    return;
                }

                navigate(`${API_DESPACHO_WEB}/attendance-channel/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const errMessage = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir o canal de atendimento "${state.name}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                AttendanceChannelService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/attendance-channel", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const errMessage = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            });
    };

    const newChannel = () => navigate(API_DESPACHO_WEB + "/attendance-channel/form", false);

    return (
        <Template isLoading={isSaving || isSavingAndAdding || isQueryLoading}>
            <Template.Header>
                <Template.Title>Canal de atendimento</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newChannel}><FaPlus/>Adicionar canal</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body className="flex flex-col gap-4">
                        <SentryRow>
                            <InputForm classNameWrapper="col-span-12" inputClass={REQUIRED_BG} id="name" label="Nome"
                                       value={state.name}
                                       labelClass={REQUIRED_LABEl_BG}
                                       onChange={({target: {value}}) => {
                                           setState(prev => ({...prev, name: value}));
                                           clearRequiredFieldError("name", value);
                                       }}
                                       reference={nameReference}
                                       {...getRequiredFieldProps("name")}/>
                        </SentryRow>
                        <InputCheckbox id="channelDefault" label="Padrão" checked={state.channelDefault}
                                       onChange={({target}) => setState(prev => ({...prev, channelDefault: target.checked}))}/>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        <Button target="primary-outline" onClick={() => persist(true)} loading={isSavingAndAdding} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist(false)} loading={isSaving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AttendanceChannelForm;
